import { useAuth0 } from "@auth0/auth0-react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { platformUsersKeys } from "../../../api/queryKeys";
import ActionStatus from "../../../components/ActionStatus";
import ContainedButton from "../../../components/buttons/ContainedButton";
import TextButton from "../../../components/buttons/TextButton";
import CustomToolTip from "../../../components/CustomTooltip";
import CustomToolTipWithPermission from "../../../components/CustomToolTipWithPermission";
import LoadingIcon from "../../../components/LoadingIcon";
import { Toast } from "../../../components/Toast";
import ToggleSwitch from "../../../components/ToggleSwitch";
import { ReactComponent as adminIcon } from "../../../icons/icon_files/admin_icon.svg";
import SvgToIcon from "../../../icons/SvgToIcon";
import { UserPlatformAccessRole } from "../../../utils/constants";
import {
  getLocalIanaTimezone,
  getUTCOffsetOfTimezone,
} from "../../../utils/timestamp";
import { ReactComponent as infoIcon } from "../../stack/environment/icons/severity_icons/severity-icons-info-tooltip-24.svg";
import { IntegrationsMapping } from "../../stack/integrations/IntegrationsMapping";
import { popularTimezonesLocationMapping } from "../constants";
import { getDisplayEmail } from "./utils";

const Profile = () => {
  const queryClient = useQueryClient();
  const [isUserOwnershipChanged, setIsUserOwnershipChanged] = useState(false);
  const [hoveredChip, setHoveredChip] = useState(null);
  const [updateUseOfLocalTimeStatus, setUpdateUseOfLocalTimeStatus] =
    useState(null);

  const {
    getPlatformUser,
    updatePlatformUserOwnerships,
    updatePlatformUserDisplayInLocalTime,
    trackEvent,
  } = useAPI();

  const { user, isAuthenticated } = useAuth0();
  const colors = useColors();

  const currentPlatformUser = useQuery({
    queryKey: platformUsersKeys.user(),
    queryFn: getPlatformUser,
    select: (response) => response.data,
  });

  const isViewer =
    currentPlatformUser.data?.access_role === UserPlatformAccessRole.VIEWER ||
    currentPlatformUser.data?.access_role ===
      UserPlatformAccessRole.MSSP_VIEWER ||
    currentPlatformUser.data?.access_role === UserPlatformAccessRole.SUPPORT;

  let displayAccessRole = currentPlatformUser.data?.access_role;
  if (currentPlatformUser.data?.access_role === UserPlatformAccessRole.MSSP) {
    displayAccessRole = UserPlatformAccessRole.ADMIN;
  } else if (
    currentPlatformUser.data?.access_role === UserPlatformAccessRole.MSSP_VIEWER
  ) {
    displayAccessRole = UserPlatformAccessRole.VIEWER;
  }

  const handleSave = () => {
    setOnSave(true);
    updatePlatformUserOwnerships(
      currentPlatformUser.data?.email,
      userOwnershipIntegrations
    ).then(() => {
      trackEvent("User updated his ownerships to " + userOwnershipIntegrations);
      Toast.success("User ownerships updated successfully");
      currentPlatformUser.data.ownerships = userOwnershipIntegrations;
      setIsUserOwnershipChanged(false);
      setOnSave(false);
    });
  };

  const handleCancel = () => {
    trackEvent("User cancel the ownerships update");
    setUserOwnershipIntegrations(currentPlatformUser.data?.ownerships);
    setIsUserOwnershipChanged(false);
  };

  const handleUpdateDisplayInLocalTime = (newValue) => {
    trackEvent(
      "User is switching the time display in local time setting to " + newValue
    );
    return updatePlatformUserDisplayInLocalTime(newValue).then(() => {
      queryClient.invalidateQueries({
        queryKey: platformUsersKeys.user(),
      });
    });
  };

  const [userOwnershipIntegrations, setUserOwnershipIntegrations] = useState(
    currentPlatformUser.data?.ownerships
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [onSave, setOnSave] = useState(false);

  const allIntegrations = Object.values(IntegrationsMapping)
    .map((integration) => {
      if (integration.available === true) {
        return integration.codeName;
      }
      return null;
    })
    .filter(Boolean);

  const availableIntegrations = allIntegrations.filter(
    (integration) => !userOwnershipIntegrations.includes(integration)
  );

  const filteredIntegrations = availableIntegrations.filter((integration) =>
    IntegrationsMapping[integration]?.displayName
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const handleIntegrationSelect = (integration) => {
    setUserOwnershipIntegrations([...userOwnershipIntegrations, integration]);
    setSearchQuery("");
    setShowDropdown(false);
    setIsUserOwnershipChanged(true);
  };

  const handleDeleteIntegration = (integration) => {
    setUserOwnershipIntegrations((prevIntegrations) =>
      prevIntegrations.filter((item) => item !== integration)
    );
    setIsUserOwnershipChanged(true);
    trackEvent("User removed ownership on integration " + integration);
  };

  const getPrettyLocalTimezoneString = () => {
    // e.g output -> "Jerusalem, Israel (UTC+3:00)"
    const timezone = getLocalIanaTimezone();
    let timezoneString;
    if (Object.keys(popularTimezonesLocationMapping).includes(timezone)) {
      const location = popularTimezonesLocationMapping[timezone];
      timezoneString = location[0];
    } else {
      const [region, city] = timezone.split("/");
      timezoneString = `${city}, ${region}`;
    }

    const offset = getUTCOffsetOfTimezone(timezone);
    const offsetString = offset > 0 ? `+${offset}` : offset;
    return `${timezoneString} (UTC${offsetString}:00)`;
  };

  return (
    <Box
      sx={{ padding: "0 8px", maxWidth: 550, margin: "auto", marginLeft: 1 }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="subtitle1" color={colors.text_primary}>
          Profile
        </Typography>
      </Box>
      <Box sx={{ marginTop: "16px", display: "flex", alignItems: "center" }}>
        <IconButton sx={{ p: 0 }}>
          <div style={{ position: "relative", display: "inline-block" }}>
            {currentPlatformUser.data?.is_admin && (
              <SvgToIcon
                IconLogo={adminIcon}
                width={20}
                style={{
                  position: "absolute",
                  top: "-2px",
                  left: "-4.5px",
                  height: "20px",
                  zIndex: 1,
                  color: colors.primary_main,
                }}
              />
            )}
            {isAuthenticated ? (
              <Avatar
                src={user.picture}
                style={{
                  width: "50px",
                  height: "52px",
                }}
              />
            ) : (
              <PersonOutlinedIcon style={{ color: colors.common_white }} />
            )}
          </div>
        </IconButton>
        <Box sx={{ marginLeft: 2 }}>
          <Typography variant="subtitle2">Name</Typography>
          <Typography variant="caption" color={colors.text_secondary}>
            {currentPlatformUser.data?.display_name}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ my: 1, width: 550 }} />
      <Box sx={{ marginBottom: 1, marginTop: 2 }}>
        <Typography variant="subtitle2">Email</Typography>
        <Typography variant="caption" color={colors.text_secondary}>
          {getDisplayEmail(currentPlatformUser.data)}
        </Typography>
      </Box>
      <Divider sx={{ my: 1, width: 550 }} />
      <Box sx={{ marginBottom: 1, marginTop: 2 }}>
        <Typography variant="subtitle2">Role</Typography>
        <Typography variant="caption" color={colors.text_secondary}>
          {displayAccessRole}
        </Typography>
      </Box>
      <Divider sx={{ my: 1, width: 550 }} />
      <Box sx={{ marginBottom: 1, marginTop: 2 }}>
        <Typography variant="subtitle2">Title</Typography>
        <Typography variant="caption" color={colors.text_secondary}>
          {currentPlatformUser.data?.role}
        </Typography>
      </Box>
      <Divider sx={{ my: 1, width: 550 }} />
      <Box
        display="flex"
        flexDirection="column"
        sx={{ marginBottom: 1, marginTop: 2 }}
        gap={1}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          height="20px"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle2">Timezone</Typography>
            <CustomToolTip
              title="This affects time display only — does not impact notification settings."
              placement="top"
              style={{ width: "270px" }}
            >
              <Box display="flex" alignItems="center">
                <SvgToIcon IconLogo={infoIcon} width={15} />
              </Box>
            </CustomToolTip>
          </Box>
          <ActionStatus
            status={updateUseOfLocalTimeStatus}
            setStatus={setUpdateUseOfLocalTimeStatus}
          />
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color={colors.text_secondary}>
            {currentPlatformUser.data?.use_localtime
              ? getPrettyLocalTimezoneString()
              : "UTC (GMT+00:00)"}
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            <Typography variant="caption" color={colors.text_primary}>
              Set timezone automatically
            </Typography>
            <ToggleSwitch
              defaultValue={currentPlatformUser.data?.use_localtime}
              updateFunction={(newValue) =>
                handleUpdateDisplayInLocalTime(newValue)
              }
              setFetchStatus={setUpdateUseOfLocalTimeStatus}
              isPermanentlyDisabled={false}
            />
          </Box>
        </Box>
      </Box>
      <Divider sx={{ my: 1, width: 550 }} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ marginBottom: 1, marginTop: 2 }}>
          <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
            Ownership
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {userOwnershipIntegrations.map((integration, index) => {
              const logoSrc = IntegrationsMapping[integration]?.logo;
              return (
                <Chip
                  key={index}
                  label={
                    <span style={{ marginLeft: -5 }}>
                      {IntegrationsMapping[integration]?.displayName}
                    </span>
                  }
                  onDelete={
                    isViewer
                      ? () => {}
                      : () => handleDeleteIntegration(integration)
                  }
                  avatar={
                    logoSrc ? <SvgToIcon IconLogo={logoSrc} width={30} /> : null
                  }
                  sx={{
                    backgroundColor: "white",
                    color: "black",
                    borderRadius: "8px",
                    border: `1px solid ${colors.grey_16percent}`,
                    "& .MuiChip-deleteIcon": {
                      visibility:
                        hoveredChip === index && !isViewer
                          ? "visible"
                          : "hidden",
                      width: "20px",
                      height: "20px",
                    },
                    marginRight: "5px",
                    height: "32px",
                  }}
                  onMouseEnter={() => setHoveredChip(index)}
                  onMouseLeave={() => setHoveredChip(null)}
                />
              );
            })}
            <Box sx={{ position: "relative" }}>
              {showDropdown ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <InputBase
                    style={{
                      height: "32px",
                      width: "160px",
                      backgroundColor: "white",
                      color: "black",
                      borderRadius: "8px",
                      border: `1px solid ${colors.grey_16percent}`,
                      paddingRight: "10px",
                      paddingLeft: "10px",
                      paddingTop: "3px",
                    }}
                    placeholder="Search"
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onBlur={() => setShowDropdown(false)}
                    autoFocus
                    endAdornment={
                      <InputAdornment position="end">
                        <AddCircleOutlineIcon
                          sx={{ color: colors.primary_main, fontSize: "16px" }}
                        />
                      </InputAdornment>
                    }
                    inputProps={{
                      style: {},
                    }}
                  />
                  <Paper
                    sx={{
                      position: "absolute",
                      zIndex: 1,
                      top: 40,
                      maxHeight: 200,
                      overflowY: "auto",
                    }}
                  >
                    <List>
                      {filteredIntegrations.map((integration, index) => (
                        <ListItem
                          button
                          key={index}
                          onMouseDown={() =>
                            handleIntegrationSelect(integration)
                          }
                          sx={{ width: "160px" }}
                        >
                          <ListItemAvatar
                            sx={{
                              minWidth: "30px",
                              maxHeight: "30px",
                              marginRight: "8px",
                              marginLeft: "-8px",
                            }}
                          >
                            {IntegrationsMapping[integration]?.logo && (
                              <SvgToIcon
                                IconLogo={
                                  IntegrationsMapping[integration]?.logo
                                }
                                width={30}
                              />
                            )}
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              IntegrationsMapping[integration]?.displayName
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Box>
              ) : (
                <CustomToolTipWithPermission
                  title={
                    filteredIntegrations.length > 0 ? "Add a new tool" : null
                  }
                  permission={!isViewer}
                  placement="top"
                >
                  <span>
                    <Chip
                      label={
                        <Box
                          sx={{
                            display: "flex",
                          }}
                        >
                          <AddCircleOutlineIcon
                            sx={{
                              color: colors.primary_main,
                              fontSize: "16px",
                            }}
                          />
                        </Box>
                      }
                      variant="outlined"
                      disabled={isViewer}
                      onClick={
                        filteredIntegrations.length > 0
                          ? () => setShowDropdown(true)
                          : null
                      }
                      sx={{
                        display: "flex",
                        height: "32px",
                        borderColor: colors.black_12percent,
                        borderRadius: "8px",
                        opacity: filteredIntegrations.length <= 0 ? 0.5 : 1,
                        cursor:
                          filteredIntegrations.length <= 0
                            ? "not-allowed"
                            : "pointer",
                      }}
                    />
                  </span>
                </CustomToolTipWithPermission>
              )}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <TextButton
            color="primary"
            onClick={handleCancel}
            disabled={!isUserOwnershipChanged}
            size="small"
          >
            Cancel
          </TextButton>
          {onSave ? (
            <LoadingIcon />
          ) : (
            <ContainedButton
              color="primary"
              onClick={handleSave}
              disabled={!isUserOwnershipChanged || onSave}
              size="small"
            >
              Save Changes
            </ContainedButton>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Profile;
